import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, GraduationCap, ArrowUpRight, LogOut } from "lucide-react";
import { Logo } from "./Logo";
import { SITE } from "../data/site";
import { useAuth, googleLogin, logout } from "../auth";

const NAV = [
  { label: "Home", href: "/#home" },
  { label: "Courses", href: "/#courses" },
  { label: "One to One", href: "/#one-to-one" },
  { label: "Roadmaps", href: "/#roadmaps" },
  { label: "Trainers", href: "/#trainers" },
  { label: "Contact Us", href: "/#contact" },
];

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/>
  </svg>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, setUser } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 backdrop-blur-xl ${
        scrolled ? "bg-[#060913]/90 border-sky-500/25" : "bg-[#060913]/60 border-sky-500/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-[72px]">
          <a href="/#home" aria-label="LearnWithTAC home" className="hover:opacity-90 transition-opacity duration-200">
            <Logo />
          </a>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3.5 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-md hover:bg-sky-500/10 transition-colors duration-200"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="header-user-chip"
                  className="inline-flex items-center gap-2.5 rounded-lg border border-sky-500/40 hover:border-sky-400 hover:bg-sky-500/10 px-3 py-2 transition-colors duration-200"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <span className="w-6 h-6 rounded-full bg-blue-600/40 flex items-center justify-center text-xs font-bold text-sky-300">
                      {user.name?.[0] || "S"}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-white max-w-[120px] truncate">{user.name?.split(" ")[0]}</span>
                </Link>
                <button
                  data-testid="header-logout-btn"
                  onClick={() => logout(setUser)}
                  aria-label="Logout"
                  className="p-2.5 rounded-lg border border-sky-500/30 text-slate-400 hover:text-orange-400 hover:border-orange-500/50 transition-colors duration-200"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                data-testid="google-signin-btn"
                onClick={googleLogin}
                className="inline-flex items-center gap-2 rounded-lg border border-sky-500/40 text-sky-300 hover:text-white hover:border-sky-400 hover:bg-sky-500/10 text-sm font-semibold px-4 py-2.5 transition-colors duration-200"
              >
                <GoogleIcon /> Sign in
              </button>
            )}
            <a
              data-testid="lms-login-btn"
              href={SITE.lmsLoginUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-5 py-2.5 transition-colors duration-200 shadow-[0_0_24px_rgba(249,115,22,0.3)]"
            >
              <GraduationCap className="w-4 h-4" />
              LMS Login
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-md text-slate-300 hover:text-white hover:bg-sky-500/10 transition-colors duration-200"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-sky-500/20 bg-[#0B1226]/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block px-3 py-2.5 rounded-md text-sm font-medium text-slate-200 hover:text-white hover:bg-sky-500/10 transition-colors duration-200"
              >
                {n.label}
              </a>
            ))}
            <div className="pt-3 space-y-3">
              {user ? (
                <div className="flex items-center gap-3">
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-user-chip"
                    className="flex-1 inline-flex items-center gap-2.5 rounded-lg border border-sky-500/40 px-4 py-3"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-blue-600/40 flex items-center justify-center text-xs font-bold text-sky-300">
                        {user.name?.[0] || "S"}
                      </span>
                    )}
                    <span className="text-sm font-semibold text-white truncate">{user.name}</span>
                  </Link>
                  <button
                    data-testid="mobile-logout-btn"
                    onClick={() => { logout(setUser); setOpen(false); }}
                    aria-label="Logout"
                    className="p-3 rounded-lg border border-sky-500/30 text-slate-400"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  data-testid="mobile-google-signin-btn"
                  onClick={googleLogin}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-sky-500/40 text-sky-300 text-sm font-semibold px-4 py-3"
                >
                  <GoogleIcon /> Sign in with Google
                </button>
              )}
              <a
                data-testid="mobile-lms-login-btn"
                href={SITE.lmsLoginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-3 transition-colors duration-200"
              >
                LMS Login
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
